import { Settings, Mic, Languages, Type, AlignJustify } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { recognitionLanguages } from '../i18n/translations';

export const SettingsPanel = ({
  t,
  recognitionLang,
  onRecognitionLangChange,
  microphones,
  selectedMic,
  onMicChange,
  fontSize,
  onFontSizeChange,
  lineSpacing,
  onLineSpacingChange,
}) => {
  const fontSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'xlarge', label: 'Extra Large' },
  ];

  const lineSpacingOptions = [
    { value: 'tight', label: 'Tight' },
    { value: 'normal', label: 'Normal' },
    { value: 'loose', label: 'Loose' },
    { value: 'double', label: 'Double' },
  ];

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="icon"
          data-testid="settings-button"
          aria-label={t('settings_title')}
        >
          <Settings className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[320px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t('settings_title')}
          </SheetTitle>
          <SheetDescription>
            Customize your transcription experience
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Recognition Language */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Languages className="h-4 w-4" />
              {t('settings_language')}
            </Label>
            <Select value={recognitionLang} onValueChange={onRecognitionLangChange}>
              <SelectTrigger data-testid="recognition-language-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {recognitionLanguages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Microphone Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mic className="h-4 w-4" />
              {t('settings_microphone')}
            </Label>
            <Select value={selectedMic || ''} onValueChange={onMicChange}>
              <SelectTrigger data-testid="microphone-selector">
                <SelectValue placeholder="Select microphone" />
              </SelectTrigger>
              <SelectContent>
                {microphones.map((mic) => (
                  <SelectItem key={mic.deviceId} value={mic.deviceId}>
                    {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Font Size */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Type className="h-4 w-4" />
              {t('settings_font_size')}
            </Label>
            <Select value={fontSize} onValueChange={onFontSizeChange}>
              <SelectTrigger data-testid="font-size-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fontSizeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Line Spacing */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <AlignJustify className="h-4 w-4" />
              {t('settings_line_spacing')}
            </Label>
            <Select value={lineSpacing} onValueChange={onLineSpacingChange}>
              <SelectTrigger data-testid="line-spacing-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {lineSpacingOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
